import React, { memo, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  Badge,
  Box,
  ButtonCore,
  ButtonIcon,
  ButtonSelect,
  Icon,
  Loader,
  MenuItem,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  CF_NEU_BLD,
  CF_NEU_STL,
  CF_WRN_STL,
  testIds,
} from '../../../../shared/constants';
import { useTypedSelector } from '../../../../shared/hooks';
import { chatModuleActions } from '../../data-store/chatModuleSlice';

import WayPointName from './WayPointName';
import WaypointProgressChip from './WaypointProgressChip';

import './ChatLeftPanel.css';

/**
 * ChatLeftPanel
 */
function ChatLeftPanel() {
  const dispatch = useDispatch();
  const { workflowInfo, isStreaming } = useTypedSelector(
    (state) => state.savicopilot.chatModule
  );

  const [isWayPointLayoutOpen, setIsWayPointLayoutOpen] = useState(false);
  const [isWorkflowStepVisiible, setIsWorkflowStepVisible] = useState(true);
  const [isEstimated, setIsEstimated] = useState(false);

  // Constants
  const isWayPointIcon = useMemo(
    () => Boolean(workflowInfo?.isWorkflow && !workflowInfo.isWorkflowComplete),
    [workflowInfo]
  );

  // Initializations
  useEffect(() => {
    if (!isWayPointIcon) {
      setIsWayPointLayoutOpen(false);
      setIsEstimated(false);

      return;
    }

    setIsWorkflowStepVisible(true);
  }, [isWayPointIcon]);

  useEffect(() => {
    if (!isWorkflowStepVisiible) {
      setIsEstimated(false);
    }
  }, [isWorkflowStepVisiible]);

  if (!isWayPointIcon || !workflowInfo) {
    return null;
  }

  const currentProgressPercentage = Math.floor(
    ((workflowInfo.currentStep - 1) / workflowInfo.steps.length) * 100
  );

  return (
    <Box
      tag='div'
      className={cx('ChatLeftPanel')}
      dataTestId={testIds.CHAT_LEFT_PANEL}>
      <Box
        tag='div'
        className={cx(
          'ChatLeftPanel-WayPointHolder',
          isWayPointLayoutOpen && 'ChatLeftPanel-WayPointHolder--isActive'
        )}>
        <Box tag='div' className='ChatLeftPanel-WayPointHolder-header'>
          <Box tag='div' className='ChatLeftPanel-WayPointButton-wrapper1'>
            <Box tag='div' className='ChatLeftPanel-WayPointButton-wrapper2'>
              <Box
                tag='div'
                className={cx(
                  'ChatLeftPanel-WayPointButton-wrapper3',
                  isWayPointLayoutOpen &&
                    'ChatLeftPanel-WayPointButton-wrapper3--isLayoutOpen'
                )}>
                <ButtonSelect
                  kind='icon'
                  ariaLabel='Trace'
                  onClick={() => {
                    setIsWayPointLayoutOpen(!isWayPointLayoutOpen);
                  }}
                  isOpen={isWayPointLayoutOpen}
                  size='small'
                  prefixIcon={<Icon kind='Helm' />}
                  BadgeComp={
                    !isWayPointLayoutOpen ? (
                      <WaypointProgressChip
                        progressPercentage={currentProgressPercentage}
                        isFixed
                      />
                    ) : null
                  }
                  isDisabled={isStreaming}
                  className={cx(
                    'ChatLeftPanel-WayPointButton',
                    isWayPointLayoutOpen &&
                      'ChatLeftPanel-WayPointButton--isLayoutOpen'
                  )}
                  dataTestId={testIds.CHAT_LEFT_PANEL_TRACE_BTN}
                />
              </Box>
            </Box>
          </Box>
          {isWayPointLayoutOpen && (
            <Box tag='div' className='ChatLeftPanel-WayPointHolder-headerText'>
              <Typography kind='h3' color={CF_NEU_BLD}>
                <FormattedMessage
                  id='vOZ+mo'
                  defaultMessage='Waypoints'
                  description='Header text for waypoints section in ChatLeftPanel component'
                />
              </Typography>
              <ButtonIcon
                size='small'
                kind='ghost'
                icon={<Icon kind='Close' size='xSmall' />}
                ariaLabel='Close waypoint'
                isDisabled={isStreaming}
                onClick={() => {
                  setIsWayPointLayoutOpen(!isWayPointLayoutOpen);
                }}
                dataTestId={testIds.CHAT_LEFT_PANEL_CLOSE_BTN}
              />
            </Box>
          )}
        </Box>
        <Box
          tag='div'
          className={cx(
            'ChatLeftPanel-WayPointLayout',
            isWayPointLayoutOpen && 'ChatLeftPanel-WayPointLayout--isActive'
          )}>
          <WayPointName
            isOpen={isWorkflowStepVisiible}
            setIsOpen={setIsWorkflowStepVisible}
            wayPointName={workflowInfo.workflowName}
            progressPercentage={currentProgressPercentage}
          />
          {isWorkflowStepVisiible && (
            <Box
              tag='div'
              className='ChatLeftPanel-WayPointLayout-workflowStepsWrapper'>
              {workflowInfo.steps.map((stepName, idx) => {
                const isInProgress = workflowInfo.currentStep - 1 === idx;
                const isAfterProgress = Boolean(
                  idx >= workflowInfo.currentStep
                );

                if (isAfterProgress) {
                  return null;
                }

                return (
                  <MenuItem
                    key={stepName.toLowerCase()}
                    option={{
                      label: stepName.toLowerCase(),
                      value: stepName.toLowerCase(),
                      isChecked: workflowInfo.currentStep - 1 === idx,
                    }}
                    prefixIcon={
                      <Box
                        tag='div'
                        className={cx(
                          'ChatLeftPanel-WayPointLayout-workflowStepsBadgeWrapper',
                          isInProgress &&
                            'ChatLeftPanel-WayPointLayout-workflowStepsBadgeWrapper--isActive'
                        )}>
                        {isInProgress ? (
                          <>
                            {isStreaming ? (
                              <Loader
                                kind='loop'
                                size='small'
                                format='inline'
                                color='brand'
                                className='ChatLeftPanel-WayPointLayout-workflowStepsLoader'
                              />
                            ) : (
                              <Icon
                                kind='StatusToDo'
                                size='xxxSmall'
                                color={CF_WRN_STL}
                                className='ChatLeftPanel-WayPointLayout-workflowStepsBadgeIcon'
                              />
                            )}
                          </>
                        ) : (
                          <Badge color='success' size='small' />
                        )}
                        {!isInProgress && (
                          <span className='ChatLeftPanel-WayPointLayout-workflowStepsConnector' />
                        )}
                      </Box>
                    }
                    onClick={() => {
                      if (!isInProgress) {
                        setIsWayPointLayoutOpen(!isWayPointLayoutOpen);

                        setTimeout(() => {
                          dispatch(chatModuleActions.setScrollToStep(idx + 1));
                        }, 100);
                      }
                    }}
                    tabIndex={0}
                    className='ChatLeftPanel-WayPointLayout-workflowStepsWrapperMenuItem'
                  />
                );
              })}
              {workflowInfo.currentStep < workflowInfo.steps.length && (
                <>
                  <ButtonCore
                    type='button'
                    tabIndex={0}
                    onClick={() => setIsEstimated(!isEstimated)}
                    ariaLabel='Estimated remaining steps'
                    isDisabled={isStreaming}
                    className='ChatLeftPanel-WayPointLayout-workflowStepsEstimatedBtn'
                    data-testid={testIds.CHAT_LEFT_PANEL_ESTIMATED_BTN}>
                    <Box
                      tag='div'
                      className='ChatLeftPanel-WayPointLayout-workflowStepsEstimatedBtnChild'>
                      <Typography kind='body3' color={CF_NEU_STL}>
                        <FormattedMessage
                          id='nyoqgW'
                          defaultMessage='Estimated remaining steps ({remainingSteps})'
                          description='Header text for waypoints section in ChatLeftPanel component'
                          values={{
                            remainingSteps:
                              workflowInfo.steps.length -
                              workflowInfo.currentStep,
                          }}
                        />
                      </Typography>
                      <Icon
                        kind={
                          isEstimated
                            ? 'ChevronCollapseAll'
                            : 'ChevronExpandAll'
                        }
                        size='small'
                      />
                    </Box>
                  </ButtonCore>
                  {isEstimated &&
                    workflowInfo.steps.map((stepName, idx) => {
                      const isAfterProgress = Boolean(
                        idx >= workflowInfo.currentStep
                      );

                      if (isAfterProgress) {
                        return (
                          <MenuItem
                            key={stepName.toLowerCase()}
                            option={{
                              label: stepName.toLowerCase(),
                              value: stepName.toLowerCase(),
                              isDisabled: true,
                            }}
                            prefixIcon={
                              <Box
                                tag='div'
                                className='ChatLeftPanel-WayPointLayout-workflowStepsBadgeWrapper'>
                                <Badge
                                  color='neutral'
                                  size='small'
                                  badgeStyle='outlined'
                                />
                                {idx !== workflowInfo.steps.length - 1 && (
                                  <span className='ChatLeftPanel-WayPointLayout-workflowStepsConnector' />
                                )}
                              </Box>
                            }
                            onClick={() => {}}
                            tabIndex={-1}
                            className='ChatLeftPanel-WayPointLayout-workflowStepsWrapperMenuItem'
                          />
                        );
                      }

                      return null;
                    })}
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default memo(ChatLeftPanel);
